"use client";

import { useState } from "react";
import { Globe, MousePointerClick, Share2, Monitor, Palette, Package, RefreshCw, Calendar, Camera, Gift, Printer, FileText, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  id: 1,
  label: "Google Business Page Management",
  icon: Globe,
  benefits: ["Boost local visibility and attract more customers", "Manage reviews and improve your online reputation", "Optimize your business profile for search results"]
}, {
  id: 2,
  label: "Google Ads Service",
  icon: MousePointerClick,
  benefits: ["Drive targeted traffic to your website", "Maximize ROI with data-driven campaigns", "Reach your audience at the right moment"]
}, {
  id: 3,
  label: "Social Media Management",
  icon: Share2,
  benefits: ["Engage and grow your audience across platforms", "Build brand awareness with consistent content", "Increase customer loyalty through interaction"]
}, {
  id: 4,
  label: "Web Development & Management",
  icon: Monitor,
  benefits: ["Create visually appealing and functional websites", "Ensure mobile responsiveness and fast load times", "Maintain and update your site regularly"]
}, {
  id: 5,
  label: "Graphic Designing & Content Creation",
  icon: Palette,
  benefits: ["Create visually appealing and impactful branding materials", "Stand out with unique designs tailored to your brand", "Deliver high-quality content that resonates"]
}, {
  id: 6,
  label: "Packaging Design & Product Development",
  icon: Package,
  benefits: ["Develop unique packaging and product branding", "Attract customers with eye-catching designs", "Ensure brand consistency across all products"]
}, {
  id: 7,
  label: "Complete Rebranding of Your Brand",
  icon: RefreshCw,
  benefits: ["Transform your brand identity with comprehensive rebranding services", "Refresh your brand to stay relevant in the market", "Align your brand with your business goals"]
}, {
  id: 8,
  label: "Event Planning",
  icon: Calendar,
  benefits: ["Plan and execute memorable events", "Manage all logistics for a seamless experience", "Create lasting impressions with your audience"]
}, {
  id: 9,
  label: "Photography & Videography",
  icon: Camera,
  benefits: ["Capture high-quality photos and videos for branding", "Showcase your products and services professionally", "Build trust with authentic visual content"]
}, {
  id: 10,
  label: "Corporate Gifts Ideas",
  icon: Gift,
  benefits: ["Innovative corporate gift ideas to impress clients", "Strengthen business relationships with thoughtful gifts", "Stand out with unique and memorable presents"]
}, {
  id: 11,
  label: "Digital Printing Services",
  icon: Printer,
  benefits: ["High-quality digital and offset printing solutions", "Fast turnaround times for all printing needs", "Professional results for marketing materials"]
}, {
  id: 12,
  label: "Offset Printing Services",
  icon: FileText,
  benefits: ["Large-scale printing with consistent quality", "Cost-effective solutions for bulk orders", "Vibrant colors and crisp details"]
}];
export default function OurServices() {
  const [selectedService, setSelectedService] = useState<number | null>(null);
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  const {
    ref: btnRef,
    isVisible: btnVisible
  } = useRevealOnScroll({
    threshold: 0.5,
    once: true
  });
  const handleCardClick = (id: number) => {
    setSelectedService(id);
  };
  const handleCloseOverlay = () => {
    setSelectedService(null);
  };
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleCloseOverlay();
    }
  };
  const selectedServiceData = services.find(s => s.id === selectedService);
  return <section className="relative bg-light-background py-16 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 text-center">
          <h2 className="font-display text-4xl font-bold text-foreground-light sm:text-5xl">
            Our Services
          </h2>
          <p className="mt-4 text-lg text-foreground-light/80">
            Comprehensive marketing solutions tailored to your business needs
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((service, i) => {
          const Icon = service.icon;
          const rowIndex = Math.floor(i / 3);
          const colIndex = i % 3;
          const isDarkRow = rowIndex % 4 === 1 || rowIndex % 4 === 2;
          const isProminent = i === 0;
          let animationClass = "";
          if (gridVisible) {
            if (colIndex === 0) {
              animationClass = "animate-fade-in-up";
            } else if (colIndex === 1) {
              animationClass = "animate-fade-in-up-delay-100";
            } else {
              animationClass = "animate-fade-in-up-delay-200";
            }
          }
          return <Card key={service.id} data-index={i} onClick={() => handleCardClick(service.id)} className={`group relative cursor-pointer overflow-hidden rounded-[var(--radius-xl)] transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${animationClass} ${isProminent ? "scale-105 border-2 border-accent shadow-[0_8px_32px_rgba(0,0,0,0.16),0_0_24px_rgba(255,215,0,0.3)] bg-gradient-to-br from-accent-light/20 to-muted-light-background animate-pulse hover:-translate-y-2 hover:shadow-[0_12px_48px_rgba(0,0,0,0.2),0_0_32px_rgba(255,215,0,0.4)]" : `border shadow-[0_2px_4px_rgba(0,0,0,0.08),0_0_12px_rgba(255,215,0,0.15)] hover:-translate-y-1.5 hover:shadow-[0_4px_16px_rgba(0,0,0,0.12),0_0_18px_rgba(255,215,0,0.25)] ${isDarkRow ? "bg-dark-background border-border-strong" : "bg-muted-light-background border-border"}`}`}>
                <CardContent className="flex flex-col items-center gap-4 p-6 text-center">
                  <div className={`flex items-center justify-center rounded-lg shadow-soft transition-transform duration-[var(--duration-medium)] ease-[var(--ease-spring)] group-hover:scale-105 ${isProminent ? "size-24 bg-accent/30 shadow-[0_4px_16px_rgba(255,215,0,0.4)]" : `size-20 ${isDarkRow ? "bg-secondary/20" : "bg-secondary-light/30"}`}`}>
                    <Icon className={`${isProminent ? "size-12 text-accent" : `size-10 ${isDarkRow ? "text-secondary" : "text-primary"}`}`} />
                  </div>
                  <h3 className={`font-display font-semibold ${isProminent ? "text-xl text-foreground-light" : `text-lg ${isDarkRow ? "text-foreground-dark" : "text-foreground-light"}`}`}>
                    {service.label}
                  </h3>
                  <div className={`absolute bottom-0 left-0 h-1 w-0 bg-accent transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] group-hover:w-full ${isProminent ? "w-full" : ""}`} />
                </CardContent>
              </Card>;
        })}
        </div>

        <div ref={btnRef} className={`mt-12 text-center transition-opacity duration-[var(--duration-slow)] ${btnVisible ? "opacity-100" : "opacity-0"}`}>
          <Button size="lg" className="btn-primary" onClick={() => {}}>
            View All Services
          </Button>
        </div>
      </div>

      {selectedService && selectedServiceData && <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm" onClick={handleOverlayClick} onKeyDown={e => {
      if (e.key === "Escape") handleCloseOverlay();
    }} role="button" tabIndex={0}>
          <div className="w-full max-w-2xl animate-fade-in-up rounded-t-[var(--radius-xl)] bg-background p-8 shadow-[0_8px_32px_rgba(0,0,0,0.16),0_0_24px_rgba(255,215,0,0.3)] border-t-2 border-accent">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="font-display text-2xl font-bold text-foreground">
                {selectedServiceData.label}
              </h3>
              <Button variant="ghost" size="icon" onClick={handleCloseOverlay} className="hover:bg-muted-light-background">
                <X className="size-5" />
              </Button>
            </div>
            <div className="space-y-4">
              <h4 className="font-semibold text-foreground">Benefits:</h4>
              <ul className="space-y-2">
                {selectedServiceData.benefits.map((benefit, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2 text-foreground/80">
                    <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-accent" />
                    <span>{benefit}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>}
    </section>;
}